import { supabase } from '@/lib/supabase';

export interface NotificationEventPreference {
  event_key:      string;
  module:         string;
  label_he:       string;
  description_he: string;
  sort_order:     number;
  in_app:         boolean;
  email:          boolean;
  sms:            boolean;
  whatsapp:       boolean;
}

export type NotificationChannel = 'in_app' | 'email' | 'sms' | 'whatsapp';

export const notificationPreferencesService = {
  async getPreferences(companyId: string): Promise<{
    data: NotificationEventPreference[] | null;
    error: Error | null;
  }> {
    const { data, error } = await supabase.rpc(
      'get_notification_preferences_for_company',
      { p_company_id: companyId },
    );
    if (error) return { data: null, error: new Error(error.message) };
    return { data: data as NotificationEventPreference[], error: null };
  },

  async upsertPreference(
    companyId: string,
    eventKey:  string,
    channels:  { in_app: boolean; email: boolean; sms: boolean; whatsapp: boolean },
  ): Promise<{ error: Error | null }> {
    const { error } = await supabase.rpc('upsert_notification_preference', {
      p_company_id: companyId,
      p_event_key:  eventKey,
      p_in_app:     channels.in_app,
      p_email:      channels.email,
      p_sms:        channels.sms,
      p_whatsapp:   channels.whatsapp,
    });
    if (error) return { error: new Error(error.message) };
    return { error: null };
  },

  async getCcContacts(): Promise<{
    data: { emails: string[]; phones: string[] };
    error: Error | null;
  }> {
    const empty = { emails: [], phones: [] };
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: empty, error: new Error('Not authenticated') };

    const { data, error } = await supabase
      .from('profiles')
      .select('notification_cc_emails, notification_cc_phones')
      .eq('id', user.id)
      .maybeSingle();

    if (error) return { data: empty, error: new Error(error.message) };
    return {
      data: {
        emails: (data?.notification_cc_emails as string[] | null) ?? [],
        phones: (data?.notification_cc_phones as string[] | null) ?? [],
      },
      error: null,
    };
  },

  async updateCcPhones(phones: string[]): Promise<{ error: Error | null }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: new Error('Not authenticated') };

    if (phones.length > 2) {
      return { error: new Error('Maximum 2 CC phones allowed') };
    }

    const { error } = await supabase
      .from('profiles')
      .update({ notification_cc_phones: phones })
      .eq('id', user.id);

    if (error) return { error: new Error(error.message) };
    return { error: null };
  },

  async updateCcEmails(emails: string[]): Promise<{ error: Error | null }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: new Error('Not authenticated') };

    if (emails.length > 2) {
      return { error: new Error('Maximum 2 CC emails allowed') };
    }

    const { error } = await supabase
      .from('profiles')
      .update({ notification_cc_emails: emails })
      .eq('id', user.id);

    if (error) return { error: new Error(error.message) };
    return { error: null };
  },
};

/** Dispatch a notification via the send-notification Edge Function.
 *  Safe to call fire-and-forget from any module action. */
export async function dispatchNotification(params: {
  event_key:   string;
  company_id:  string;
  user_ids:    string[];
  title:       string;
  message:     string;
  action_url?: string;
  payload?:    Record<string, unknown>;
}): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  fetch(`${supabaseUrl}/functions/v1/send-notification`, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'Apikey':        anonKey,
    },
    body: JSON.stringify(params),
  }).catch((err) => {
    console.warn('[dispatchNotification] fire-and-forget failed:', err);
  });
}

/** Dispatch a notification to B2B clients via the send-notification Edge Function.
 *  Uses b2b_client_ids instead of user_ids (B2B mode). */
export async function dispatchB2BNotification(params: {
  event_key:       string;
  company_id:      string;
  b2b_client_ids:  string[];
  title:           string;
  message:         string;
  action_url?:     string;
  payload?:        Record<string, unknown>;
}): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  fetch(`${supabaseUrl}/functions/v1/send-notification`, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'Apikey':        anonKey,
    },
    body: JSON.stringify(params),
  }).catch((err) => {
    console.warn('[dispatchB2BNotification] fire-and-forget failed:', err);
  });
}
